"use client";

import React, { useState, useCallback } from "react";
import { useLocale, useTranslations } from "next-intl";
import { useSearchParams, useRouter, usePathname } from "next/navigation";
import { debounce } from "lodash";
import { Button } from "../ui/button";
import { X } from "lucide-react";
import { Slider } from "../ui/slider";

const MIN_PRICE = 0;
const MAX_PRICE = 1_000_000;
const STEP = 100;

const QUICK_RANGES: [number, number][] = [
  [0, 1000],
  [1000, 3000],
  [3000, 5000],
  [5000, 10000],
  [10000, 50000],
  [50000, 100000],
  [100000, MAX_PRICE],
];

const PriceRange = () => {
  const t = useTranslations();
  const locale = useLocale();
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();

  /** 🔹 Start with FULL RANGE – no URL involved */
  const [localPriceRange, setLocalPriceRange] = useState<[number, number]>([
    MIN_PRICE,
    MAX_PRICE,
  ]);

  const [isActive, setIsActive] = useState(false);

  /* ---------------------------------------------
   * Update URL (ONLY when user changes)
   * --------------------------------------------*/
  const updateSearchParams = useCallback(
    debounce((min: number, max: number) => {
      const params = new URLSearchParams(searchParams.toString());

      params.set("min_price", min.toString());
      params.set("max_price", max.toString());

      router.push(`${pathname}?${params.toString()}`, { scroll: false });
    }, 500),
    [searchParams, pathname, router],
  );

  /* ---------------------------------------------
   * Handlers
   * --------------------------------------------*/
  const handleSliderChange = (values: number[]) => {
    setLocalPriceRange(values as [number, number]);
  };

  const handleSliderCommit = (values: number[]) => {
    const [min, max] = values as [number, number];

    setIsActive(min !== MIN_PRICE || max !== MAX_PRICE);
    updateSearchParams(min, max);
  };

  const clearFilters = () => {
    setLocalPriceRange([MIN_PRICE, MAX_PRICE]);
    setIsActive(false);

    const params = new URLSearchParams(searchParams.toString());
    params.delete("min_price");
    params.delete("max_price");

    router.push(`${pathname}?${params.toString()}`, { scroll: false });
  };

  /* ---------------------------------------------
   * Utils
   * --------------------------------------------*/
  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "SYP",
      minimumFractionDigits: 0,
    }).format(value);

  const getDisplayRange = () => {
    const [min, max] = localPriceRange;

    if (min !== MIN_PRICE || max !== MAX_PRICE) {
      return `${formatCurrency(min)} – ${formatCurrency(max)}`;
    }

    return t("allPrices");
  };

  /* ---------------------------------------------
   * Render
   * --------------------------------------------*/
  return (
    <div className="space-y-4">
      <div className="flex justify-between">
        {isActive && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearFilters}
            className="h-8 px-2 text-xs text-gray-500 hover:text-gray-700"
          >
            <X className="h-3 w-3 mr-1" />
            {t("clear")}
          </Button>
        )}
      </div>

      <div className="space-y-3">
        {/* Current range */}
        <div className="text-center">
          <div className="text-lg font-semibold">{getDisplayRange()}</div>
          <div className="text-xs text-gray-500 mt-1">
            {isActive ? t("filteringBy") : t("dragToSetPriceRange")}
          </div>
        </div>

        {/* Slider */}
        <div className="pt-4 px-2">
          <Slider
            dir={locale === "ar" ? "rtl" : "ltr"}
            value={localPriceRange}
            min={MIN_PRICE}
            max={MAX_PRICE}
            step={STEP}
            minStepsBetweenThumbs={1}
            onValueChange={handleSliderChange}
            onValueCommit={handleSliderCommit}
            className="w-full"
          />

          <div className="flex justify-between text-xs text-gray-500 mt-3">
            <span>{formatCurrency(MIN_PRICE)}</span>
            <span>{formatCurrency(MAX_PRICE)}+</span>
          </div>
        </div>

        {/* Quick ranges */}
        <div className="flex flex-wrap gap-2 pt-2">
          {QUICK_RANGES.map(([min, max]) => {
            const isSelected =
              localPriceRange[0] === min && localPriceRange[1] === max;

            return (
              <Button
                key={`${min}-${max}`}
                size="sm"
                variant={isSelected ? "default" : "outline"}
                className="h-7 text-xs"
                onClick={() => {
                  setLocalPriceRange([min, max]);
                  setIsActive(true);
                  updateSearchParams(min, max);
                }}
              >
                {min === 0
                  ? `${t("under")} ${formatCurrency(max)}`
                  : max === MAX_PRICE
                    ? `${formatCurrency(min)}+`
                    : `${formatCurrency(min)} – ${formatCurrency(max)}`}
              </Button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PriceRange;
